import Carousel, { slidesToShowPlugin, arrowsPlugin } from '@brainhubeu/react-carousel'
import { Box, Container, Divider, Typography } from '@material-ui/core'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import iconOrderFinished from 'assets/images/icon-orderFinished.svg'
import { useEffect, useState, useRef } from 'react'
import { AiOutlineCheck, AiOutlineProfile } from 'react-icons/ai'
import logoFrexco from 'assets/icons/logo-frexco.png'
import logoIfood from 'assets/icons/logo-ifood.png'

import { useStyles } from './styles'
import { IOrdersResume } from 'interfaces/IDashboard'
import { FaBoxes, FaPeopleCarry } from 'react-icons/fa'
import { GiWeight } from 'react-icons/gi'
import { MdOutlineSpeed } from 'react-icons/md'

interface ISummaryProps {
  totalItens: number
  totalOrders: number
  totalIfoodOrders: IOrdersResume
  totalFrexcoOrders: IOrdersResume
  completed: number
  separationOrders: number
  totalVolume: number
  processedVolume: number
  lastUpdate: string | null
  className?: string
  pendentOrders: number
  tonsPerHour: number
  viewMode?: 'orders' | 'items'
  processedItems?: number
  itemsInProgress?: number
  itemsRemaining?: number
  itemsCompletionPercentage?: number
  ordersCompletionPercentage?: number
  historicalComparison?: {
    created: string | null
    previous_total_orders: number
    previous_total_items: number
    orders_percentage_change: number
    items_percentage_change: number
    previous_total_volume: number
    total_volume_percentage_change: number
  }
}

export const Summary = (props: ISummaryProps) => {
  const {
    completed,
    totalItens,
    totalOrders,
    className,
    separationOrders,
    totalVolume,
    processedVolume,
    lastUpdate,
    pendentOrders,
    tonsPerHour,
    viewMode = 'orders',
    processedItems = 0,
    itemsInProgress = 0,
    itemsRemaining = 0,
    itemsCompletionPercentage = 0,
    ordersCompletionPercentage = 0,
    historicalComparison,
  } = props

  const [index, setIndex] = useState(0)
  const [showTonsPerHour, setShowTonsPerHour] = useState(false)
  const [isBoxFixed, setIsBoxFixed] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(() => window?.innerWidth)
  const classes = useStyles({ isCarrouselVisible: width <= 1200 })
  const plugin = {
    resolve: arrowsPlugin,
    options: {
      arrowLeft: (
        <button className={classes.arrowLeft}>
          <ArrowBackIosIcon className={classes.arrowIcon} />
        </button>
      ),
      arrowLeftDisabled: (
        <button disabled={true} className={classes.arrowLeft}>
          <ArrowBackIosIcon className={classes.arrowIcon} />
        </button>
      ),
      arrowRight: (
        <button className={classes.arrowRight}>
          <ArrowForwardIosIcon className={classes.arrowIcon} />
        </button>
      ),
      arrowRightDisabled: (
        <button disabled={true} className={classes.arrowRight}>
          <ArrowForwardIosIcon className={classes.arrowIcon} />
        </button>
      ),
      addArrowClickHandler: true,
    },
  }

  const TotalCount = () => {
    const isOrdersView = viewMode === 'orders'
    const currentValue = isOrdersView ? totalOrders : totalItens
    const previousValue = isOrdersView 
      ? historicalComparison?.previous_total_orders 
      : historicalComparison?.previous_total_items
    const percentageChange = isOrdersView
      ? historicalComparison?.orders_percentage_change
      : historicalComparison?.items_percentage_change

    const renderComparison = () => {
      if (!historicalComparison || previousValue === undefined) return null
      
      const isPositive = (percentageChange ?? 0) >= 0
      const sign = isPositive ? '+' : ''
      
      return (
        <Typography className={classes.comparison}>
          Semana anterior: {previousValue} {percentageChange && (`(${sign}${percentageChange?.toFixed(2)}%)`)}
        </Typography>
      )
    }

    return (
      <Box className={classes.box}>
        <span className={`${classes.icon} ${classes.totalOrderIcon}`}>
          <FaBoxes style={{ color: '#ffffff', fontSize: '1.3rem' }} />
        </span>
        <div>
          <Typography className={classes.title}>
            {isOrdersView ? 'Total de pedidos' : 'Total de itens'}
          </Typography>
          <Typography className={classes.subtitle}>
            {currentValue ?? 0}
          </Typography>
          {renderComparison()}
        </div>
      </Box>
    )
  }

  const CompletedCount = () => {
    const getFinishedText = () => {
      if (viewMode === 'orders') {
        return completed ?? 0
      }
      
      // Modo itens
      const itemsText = (processedItems ?? 0).toLocaleString('pt-BR')
      
      return itemsText
    }

    const getPercentageText = () => {
      if (viewMode === 'orders' && ordersCompletionPercentage > 0) {
        return ` (${ordersCompletionPercentage.toFixed(2)}%)`
      }
      
      if (viewMode === 'items' && itemsCompletionPercentage > 0) {
        return ` (${itemsCompletionPercentage.toFixed(2)}%)`
      }
      
      return ''
    }

    return (
      <Box className={classes.box}>
        <span className={`${classes.icon} ${classes.ordersFinished}`}>
          <img loading="lazy" src={iconOrderFinished} />
        </span>
        <div>
          <Typography className={classes.title}>
            {viewMode === 'orders' ? 'Pedidos finalizados' : 'Itens finalizados'}
          </Typography>
          <Typography className={classes.subtitle}>
            {getFinishedText()}{getPercentageText()}
          </Typography>
        </div>
      </Box>
    )
  }

  const InProgressCount = () => (
    <Box className={classes.box}>
      <span className={`${classes.icon} ${classes.separationOrders}`}>
        <FaPeopleCarry style={{ color: '#ffffff', fontSize: '1.3rem' }} />
      </span>
      <div>
        <Typography className={classes.title}>
          {viewMode === 'orders' ? 'Pedidos em separação' : 'Itens em separação'}
        </Typography>
        <Typography className={classes.subtitle}>
          {viewMode === 'orders' ? (separationOrders ?? 0) : (itemsInProgress ?? 0).toLocaleString('pt-BR')}
        </Typography>
      </div>
    </Box>
  )

  const PendingCount = () => (
    <Box className={classes.box}>
      <span className={`${classes.icon} ${classes.ordersPending}`} style={{ marginTop: '0.9rem' }}>
        <MoreHorizIcon style={{ color: '#ffffff' }} />
      </span>
      <div>
        <Typography className={classes.title}>
          {viewMode === 'orders' ? 'Pedidos pendentes' : 'Itens pendentes'}
        </Typography>
        <Typography className={classes.subtitle}>
          {viewMode === 'orders' ? (pendentOrders || 0) : (itemsRemaining || 0).toLocaleString('pt-BR')}
        </Typography>
        <Typography className={classes.lastUpdate}>Atualizado às: {lastUpdate}</Typography>
      </div>
    </Box>
  )

  const TotalVolume = () => {
    const previousValue = historicalComparison?.previous_total_volume
    const percentageChange = historicalComparison?.total_volume_percentage_change

    const renderComparison = () => {
      if (!historicalComparison || previousValue === undefined) return null
      
      const isPositive = (percentageChange ?? 0) >= 0
      const sign = isPositive ? '+' : ''
      
      return (
        <Typography className={classes.comparison}>
          Semana anterior: {Math.floor(previousValue).toLocaleString()} kg {percentageChange && (`(${sign}${percentageChange?.toFixed(2)}%)`)}
        </Typography>
      )
    }

    return (
      <Box className={classes.box}>
        <span className={`${classes.icon} ${classes.ordersTotal}`}>
          <GiWeight style={{ color: '#ffffff', fontSize: '1.4rem' }} />
        </span>
        <div>
          <Typography className={classes.title}>Volume total</Typography>
          <Typography className={classes.subtitle}>
            {`${Math.floor(totalVolume).toLocaleString() || '0'} kg`}
          </Typography>
          {renderComparison()}
        </div>
      </Box>
    )
  }

  const VolumeProcessed = () => (
    <div ref={boxRef}>
      <Box
        className={classes.box}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {showTonsPerHour || isBoxFixed ? (
          <>
            <span className={`${classes.icon} ${classes.ordersProcessed}`}>
              <MdOutlineSpeed style={{ color: '#ffffff', fontSize: '1.4rem' }} />
            </span>
            <div>
              <Typography className={classes.title}>Toneladas por Hora</Typography>
              <Typography className={classes.subtitle}>{`${
                tonsPerHour.toFixed(3).replace('.', ',') || '0'
              } T/h`}</Typography>
            </div>
          </>
        ) : (
          <>
            <span className={`${classes.icon} ${classes.ordersProcessed}`}>
              <AiOutlineCheck style={{ color: '#ffffff', fontSize: '1.3rem' }} />
            </span>
            <div>
              <Typography className={classes.title}>Volume processado</Typography>
              <Typography className={classes.subtitle}>
                {`${Math.floor(processedVolume).toLocaleString() || '0'} kg`}
              </Typography>
            </div>
          </>
        )}
      </Box>
    </div>
  )

  const handleMouseEnter = () => {
    if (!isBoxFixed) setShowTonsPerHour(true)
  }

  const handleMouseLeave = () => {
    if (!isBoxFixed) setShowTonsPerHour(false)
  }

  const handleClick = () => {
    setIsBoxFixed(!isBoxFixed)
    setShowTonsPerHour(!isBoxFixed)
  }

  useEffect(() => {
    const updateWidth = (e: any) => {
      setWidth(e.target?.innerWidth)
    }

    window.addEventListener('resize', updateWidth)

    return () => {
      window.removeEventListener('resize', updateWidth)
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setIsBoxFixed(false)
        setShowTonsPerHour(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [boxRef])

  useEffect(() => {
    const updateWidth = (e: any) => {
      setWidth(e.target?.innerWidth)
    }

    window.addEventListener('resize', updateWidth)

    return () => {
      window.removeEventListener('resize', updateWidth)
    }
  }, [])

  return (
    <>
      <Carousel
        className={`${classes.carousel} ${className}`}
        value={index}
        onChange={e => {
          if (e >= 0) {
            setIndex(e)
          }
        }}
        plugins={[
          {
            resolve: slidesToShowPlugin,
            options: { numberOfSlides: 3 },
          },
          plugin,
        ]}
        breakpoints={{
          820: {
            plugins: [
              {
                resolve: slidesToShowPlugin,
                options: { numberOfSlides: 2 },
              },
              plugin,
            ],
          },
          520: {
            plugins: [
              {
                resolve: slidesToShowPlugin,
                options: { numberOfSlides: 1 },
              },
              plugin,
            ],
          },
        }}
      >
        <div className={classes.ordersDiv}>
          <TotalCount />
        </div>


        <div style={{ display: 'flex' }}>
          <CompletedCount />

          <Divider className={classes.divider} orientation="vertical" flexItem />
        </div>

        <div style={{ display: 'flex' }}>
          <InProgressCount />

          <Divider className={classes.divider} orientation="vertical" flexItem />
        </div>

        <div style={{ display: 'flex' }}>
          <PendingCount />
        </div>

        <div style={{ display: 'flex' }}>
          <TotalVolume />
        </div>

        <div style={{ display: 'flex' }}>
          <VolumeProcessed />
        </div>
      </Carousel>

      <Container className={`${classes.root} ${className}`}>
        <TotalCount />

        <Divider className={classes.divider} />

        <CompletedCount />

        <Divider className={classes.divider} />

        <InProgressCount />

        <Divider className={classes.divider} />

        <PendingCount />

        <Divider className={classes.divider} />

        <TotalVolume />

        <Divider className={classes.divider} />

        <VolumeProcessed />
      </Container>
    </>
  )
}
